import React, { useState, useEffect } from 'react'
import { OmList, OmListItem, OmCheckbox } from '@owma/kit'

import PerfectScrollbar from 'libs/react-perfect-scrollbar'

import classNames from 'classnames'

import { EmptyData } from 'utils/ui'

import dataManager from 'pages/main/data/DataManager'

import styles from './style.module.scss'

interface EmailItem {
  id: string
  name: string
  email: string
  avatarUrl: string
}

interface RecentItemProps {
  item: EmailItem
  checkBoxElem: React.ReactElement
}

function RecentItem(props: RecentItemProps): React.ReactElement {
  const { item, checkBoxElem } = props
  const { name, email, avatarUrl } = item

  const [imgSrc, setImgSrc] = useState<string>(avatarUrl)

  useEffect(() => {
    dataManager
      .getAddressObj(email, name)
      .getInfoForAvatar()
      .then((info: any) => {
        setImgSrc(info.avatarUrl)
      })
  }, [])

  const nameElem = <div className={styles.name}>{name}</div>
  const emailElem = <div className={styles.email}>{email}</div>

  return (
    <React.Fragment>
      <div className={styles.icon}>
        <img className={classNames(styles.avatar)} src={imgSrc} alt="avatar" />
      </div>
      <div className={styles.info}>
        {nameElem}
        {emailElem}
      </div>
      {checkBoxElem}
    </React.Fragment>
  )
}

interface RecentEmailListProps {
  innerRef?: React.RefObject<any>
  type?: string
  category?: string
  excludeEmailList?: string[]
  defaultEmailList?: string[]
  disableEmails?: string[]
  enableSelect?: boolean
  onSelectedChange?: (id: string, type: string, item: EmailItem) => void
  onSelectChanged?: (emailList: string[], userList: EmailItem[]) => void
}

interface RecentEmailListState {
  viewEmailList: EmailItem[]
  checkedKeys: string[]
  selectedKey?: string
}

class RecentEmailList extends React.Component<RecentEmailListProps, RecentEmailListState> {
  state: RecentEmailListState = {
    viewEmailList: [],
    checkedKeys: [],
  }

  checkedKeys: string[] = []

  emailMap: Record<string, EmailItem> = {}

  disableEmails: string[] = this.props.disableEmails?.map((email) => email.toLowerCase()) || []

  searchText?: string

  searchTimer?: NodeJS.Timeout

  scrollRef = React.createRef<any>()

  componentDidMount(): void {
    const { innerRef } = this.props

    if (innerRef != null) {
      ;(innerRef as React.MutableRefObject<any>).current = this.createRefObj()
    }

    this.initList()
  }

  componentWillUnmount(): void {}

  createRefObj = (): any => {
    const obj = {
      onSearch: (text: string) => {
        return this.onSearch(text)
      },
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        return this.onChange(e)
      },
    }

    return obj
  }

  initList = (searchText?: string): void => {
    const { type, category } = this.props

    console.log('initList - ', type, category)

    this.checkedKeys = []

    this.emailMap = {}

    this.searchText = searchText

    this.loadData()
  }

  loadData = (): void => {
    const { excludeEmailList, defaultEmailList } = this.props

    dataManager.getRecentSendEmailList().then((res: EmailItem[]) => {
      console.log('res - ', res)

      let emailList = res || []

      emailList = emailList.filter((emailItem) => {
        const { id, email } = emailItem
        this.emailMap[id] = emailItem
        return excludeEmailList == null || excludeEmailList.indexOf(email) < 0
      })

      if (this.searchText != null && this.searchText.trim().length > 0) {
        emailList = emailList.filter((emailItem) => {
          const { email, name } = emailItem
          return email.indexOf(this.searchText!) > -1 || name.indexOf(this.searchText!) > -1
        })
      }

      const newState: Partial<RecentEmailListState> = {
        viewEmailList: emailList,
      }

      if (defaultEmailList != null && defaultEmailList.length > 0) {
        this.checkedKeys = [...emailList]
          .filter((item) => {
            return defaultEmailList.indexOf(item.email) > -1
          })
          .map((item) => {
            return item.id
          })
        newState.checkedKeys = [...this.checkedKeys]
      }

      this.setState(newState as RecentEmailListState)
    })
  }

  onSearch = (value: string): void => {
    this.initList(value)
  }

  onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    console.log('onChange - ', e)
    const { value } = e.target
    if (this.searchTimer != null) {
      clearTimeout(this.searchTimer)
    }

    this.searchTimer = setTimeout(() => {
      this.searchTimer = undefined
      this.onSearch(value)
    }, 300)
  }

  onItemClick = (id: string, item: EmailItem): void => {
    const { enableSelect, onSelectedChange, type } = this.props
    console.log('onItemClick - ', id, item)

    if (enableSelect === true) {
      this.setState({
        selectedKey: id,
      })

      if (onSelectedChange != null) {
        onSelectedChange(id, type!, item)
      }
    }

    this.toggleChecked(id)
  }

  toggleChecked = (id: string): void => {
    const { onSelectChanged } = this.props

    const index = this.checkedKeys.indexOf(id)
    if (index > -1) {
      this.checkedKeys.splice(index, 1)
    } else {
      this.checkedKeys.push(id)
    }

    this.setState({
      checkedKeys: [...this.checkedKeys],
    })

    if (onSelectChanged != null) {
      const checkedEmails: string[] = []
      const checkedUsers: EmailItem[] = []
      this.checkedKeys.forEach((key) => {
        const user = this.emailMap[key]
        if (user != null && checkedEmails.indexOf(key) < 0) {
          checkedEmails.push(user.email)
          checkedUsers.push(user)
        }
      })

      onSelectChanged(checkedEmails, checkedUsers)
    }
  }

  onScrollY = (..._args: any[]): void => {}

  onYReachStart = (..._args: any[]): void => {}

  renderItem = (item: EmailItem): React.ReactElement => {
    const { selectedKey } = this.state
    const { enableSelect } = this.props

    const { id } = item

    const clsProps: any = {}

    if (enableSelect === true) {
      clsProps.selected = selectedKey === id
    }

    const checkBoxAttrs = {
      checked: this.checkedKeys.indexOf(id) > -1,
    }

    const checkBoxElem = (
      <div className={styles.check}>
        <OmCheckbox
          checked={checkBoxAttrs.checked}
          disabled={this.disableEmails.indexOf(item.email) > -1}
        />
      </div>
    )

    return (
      <OmListItem
        key={id}
        className={classNames(styles.emailItem, clsProps)}
        onClick={() => {
          if (this.disableEmails.indexOf(item.email) > -1) {
            return
          }
          this.onItemClick(id, item)
        }}
      >
        <RecentItem item={item} checkBoxElem={checkBoxElem} />
      </OmListItem>
    )
  }

  render(): React.ReactElement {
    const { viewEmailList } = this.state

    return (
      <div className={classNames(styles.emailList, 'emailList', 'small')}>
        <PerfectScrollbar
          className={styles.scrollView}
          refScroll={this.scrollRef}
          height="100%"
          onScrollY={this.onScrollY}
          onYReachStart={this.onYReachStart}
        >
          <OmList
            dataSource={viewEmailList}
            emptyText={<EmptyData type="search" mailbox={null} />}
            renderItem={this.renderItem}
          />
        </PerfectScrollbar>
      </div>
    )
  }
}

export default RecentEmailList
